import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { api } from "./api";

const todayIso = () => new Date().toLocaleDateString("en-CA");
const fmtHours = (h) => Number(Number(h || 0).toFixed(2));

function dateLabel(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

// Filter by: [value, label, label of the "no value" option].
const FILTERS = [
  ["department", "Departments", "All departments"],
  ["project", "Projects", "All projects"],
  ["manager", "Reporting Manager", "All reporting managers"],
];

// [header, field] in table and Excel column order.
const COLUMNS = [
  ["Emp ID", "empid"],
  ["Employee Name", "empname"],
  ["Employee Email", "employeeemail"],
  ["Department", "department"],
  ["Reporting Manager ID", "reportingmanagerempid"],
  ["Reporting Manager Name", "reportingmanagername"],
  ["Reporting Manager Email", "reportingmanageremail"],
  ["Date", "dateofentry"],
  ["Project", "projectname"],
  ["Description", "description"],
  ["Hours", "hoursworked"],
  ["Status", "status"],
];

function downloadExcel(rows, { fromdate, todate, label }) {
  const data = rows.map((r) =>
    Object.fromEntries(COLUMNS.map(([h, f]) => [h, f === "hoursworked" ? fmtHours(r[f]) : r[f] ?? ""]))
  );
  const worksheet = XLSX.utils.json_to_sheet(data, { header: COLUMNS.map(([h]) => h) });
  // Column width from the longest value in each column, capped so Description stays readable.
  worksheet["!cols"] = COLUMNS.map(([h]) => ({
    wch: Math.min(60, Math.max(h.length, ...data.map((d) => String(d[h]).length)) + 2),
  }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Timesheet");
  const suffix = label ? `_${label.replace(/\s*\(.*\)$/, "").replace(/[^A-Za-z0-9]+/g, "_")}` : "";
  XLSX.writeFile(workbook, `timesheet_${fromdate}_to_${todate}${suffix}.xlsx`);
}

export default function Download({ userDetails }) {
  const [fromdate, setFromdate] = useState(() => `${todayIso().slice(0, 7)}-01`);
  const [todate, setTodate] = useState(todayIso);
  const [filterby, setFilterby] = useState(""); // "" = no filter
  const [filtervalue, setFiltervalue] = useState("");
  const [filterOptions, setFilterOptions] = useState([]);
  const [rows, setRows] = useState(null); // null until the first search
  const [range, setRange] = useState(null); // the dates `rows` belong to
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Second dropdown: departments / reporting managers found in the date range, or all projects.
  useEffect(() => {
    setFiltervalue("");
    setFilterOptions([]);
    if (!filterby || !fromdate || !todate || fromdate > todate) return undefined;
    let cancelled = false;
    api
      .getDownloadFilters(userDetails.EmpEmail, fromdate, todate, filterby)
      .then((list) => { if (!cancelled) setFilterOptions(list); })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [userDetails.EmpEmail, fromdate, todate, filterby]);

  const filter = FILTERS.find(([v]) => v === filterby);

  const search = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      setRows(await api.getDownloadEntries(userDetails.EmpEmail, fromdate, todate, filterby, filtervalue));
      // The chosen filter value's label, for the summary line and the Excel file name.
      const label = filtervalue ? filterOptions.find((o) => o.value === filtervalue)?.label || filtervalue : "";
      setRange({ fromdate, todate, label });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const totalHours = (rows || []).reduce((sum, r) => sum + Number(r.hoursworked), 0);

  return (
    <section className="timesheet-container">
      <div className="timesheet-head">
        <h2>Download</h2>
      </div>

      <form className="timesheet-entry-box" onSubmit={search}>
        <div className="timesheet-entry-title">Submitted and accepted time entries of all employees</div>
        <div className="timesheet-entry-fields">
          <label className="timesheet-field timesheet-field-date">
            <span>From date</span>
            <input required type="date" value={fromdate} max={todate} onChange={(e) => setFromdate(e.target.value)} />
          </label>
          <label className="timesheet-field timesheet-field-date">
            <span>To date</span>
            <input required type="date" value={todate} min={fromdate} onChange={(e) => setTodate(e.target.value)} />
          </label>
          <label className="timesheet-field timesheet-field-filter">
            <span>Filter by</span>
            <select value={filterby} onChange={(e) => setFilterby(e.target.value)}>
              <option value="">None</option>
              {FILTERS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
          </label>
          <label className="timesheet-field timesheet-field-filter">
            <span>{filter ? filter[1] : "Value"}</span>
            <select value={filtervalue} onChange={(e) => setFiltervalue(e.target.value)} disabled={!filter}>
              <option value="">{filter ? filter[2] : "Choose a filter first"}</option>
              {filterOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <div className="timesheet-field timesheet-field-action">
            <span aria-hidden="true">&nbsp;</span>
            <button className="timesheet-primary" type="submit" disabled={loading}>
              {loading ? "Loading…" : "Show records"}
            </button>
          </div>
          <div className="timesheet-field timesheet-field-action">
            <span aria-hidden="true">&nbsp;</span>
            <button
              type="button"
              className="timesheet-download"
              disabled={!rows || rows.length === 0}
              onClick={() => downloadExcel(rows, range)}
            >
              Download Excel
            </button>
          </div>
        </div>
      </form>

      {error && <div className="timesheet-error">{error}</div>}

      {rows && (
        <>
          <p className="timesheet-review-count timesheet-download-summary">
            {rows.length} records · {fmtHours(totalHours)} h · {dateLabel(range.fromdate)} to {dateLabel(range.todate)}
            {range.label && ` · ${range.label}`}
          </p>
          <div className="timesheet-table-wrap timesheet-download-table">
            <table className="timesheet-table">
              <thead>
                <tr>
                  <th className="timesheet-col-index">#</th>
                  {COLUMNS.map(([h, f]) => (
                    <th key={f} className={f === "hoursworked" ? "timesheet-num" : ""}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={COLUMNS.length + 1} className="timesheet-table-empty">No records in this date range.</td></tr>
                ) : (
                  rows.map((r, i) => (
                    <tr key={r.id}>
                      <td className="timesheet-col-index">{i + 1}</td>
                      <td className="timesheet-nowrap">{r.empid}</td>
                      <td className="timesheet-nowrap">{r.empname}</td>
                      <td className="timesheet-nowrap">{r.employeeemail}</td>
                      <td className="timesheet-nowrap">{r.department}</td>
                      <td className="timesheet-nowrap">{r.reportingmanagerempid}</td>
                      <td className="timesheet-nowrap">{r.reportingmanagername}</td>
                      <td className="timesheet-nowrap">{r.reportingmanageremail}</td>
                      <td className="timesheet-nowrap">{dateLabel(r.dateofentry)}</td>
                      <td className="timesheet-nowrap">{r.projectname}</td>
                      <td className="timesheet-description">{r.description}</td>
                      <td className="timesheet-num">{fmtHours(r.hoursworked)}</td>
                      <td>
                        <span className={`timesheet-status-tag ${r.status === "Accepted" ? "accepted" : "submitted"}`}>
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
